import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ChefHat, ChevronRight, Info, Loader2 } from 'lucide-react'
import apiClient from '@/api/client'
import { toastHelpers } from '@/lib/toast-helpers'
import { useCurrency } from '@/contexts/CurrencyContext'
import { useCategories } from '@/hooks/useCategories'
import { currencyInputPrefix, formatMoney } from '@/lib/formatMoney'
import { COSTING_STATUS_LABELS, costingStatus, packagingOnly, type CostingStatus } from '@/lib/recipeEditor'
import { walkSnapshot } from '@/lib/costingSheetState'
import { cn } from '@/lib/utils'
import { ProductImagePlaceholder } from '@/components/menu/ProductImagePlaceholder'
import type { Product, Category, ProjectedCostBasis, ProductCostingHealth } from '@/types'
import { CostingSheet, type CopyCandidate } from './costing/CostingSheet'
import { SellAsIsCard } from './costing/SellAsIsCard'
import { MenuImportCard } from './costing/MenuImportCard'
import { useCogsBasis } from '@/components/admin/expenses/use-cogs-basis'
import { ITEM_COSTS_REFERENCE_ONLY } from '@/components/finance/cogsBasisDisplay'

/**
 * Costing & Recipes (F-FE-N157).
 *
 * The list stayed what it was — every item, what it costs you, the margin, and
 * the signals that explain a surprise (F-RPT-N25). What changed is that every
 * row now OPENS: the sheet is where the owner picks how the item is costed and
 * edits the recipe in place. Before this the tab was read-only and the recipe
 * editor sat three levels deep inside the product form; the owner saw the
 * problem here and had to fix it somewhere else.
 */

type StatusFilter = 'all' | 'none' | 'attention' | 'recipe' | 'manual'

const STATUS_CLASS: Record<CostingStatus, string> = {
  none: 'border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-300',
  attention: 'border-destructive/40 text-destructive',
  recipe: 'border-emerald-300 text-emerald-700 dark:border-emerald-700 dark:text-emerald-300',
  fixed: 'border-sky-300 text-sky-700 dark:border-sky-700 dark:text-sky-300',
  percent: 'border-sky-300 text-sky-700 dark:border-sky-700 dark:text-sky-300',
}

/** Why a row needs attention — the tooltip on the chip and the line under the name. */
function attentionReasons(h: ProductCostingHealth | undefined): string[] {
  if (!h || h.component_count === 0) return []
  const out: string[] = []
  if (h.recipe_cost > 0 && h.price > 0 && h.recipe_cost > h.price) out.push('Costs more than it sells for')
  if (h.components_missing_cost > 0) out.push(`${h.components_missing_cost} ingredient${h.components_missing_cost === 1 ? '' : 's'} without a cost`)
  if (packagingOnly(h.recipe_cost, h.projected_cost)) out.push('Recipe cost far below your own figure')
  return out
}

function productHasManualCost(p: Product): boolean {
  return p.projected_cost != null
}

export function AdminMenuCostingTab({
  itemId,
  onSelectItem,
}: {
  /** Product whose sheet is open (from the URL), or null. */
  itemId: string | null
  onSelectItem: (id: string | null) => void
}) {
  const qc = useQueryClient()
  const { currencyCode } = useCurrency()
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkPercent, setBulkPercent] = useState('')
  const [bulkBasis, setBulkBasis] = useState<ProjectedCostBasis>('percent')
  const [bulkAmount, setBulkAmount] = useState('')
  const [bulkBusy, setBulkBusy] = useState(false)
  // The order Prev / Next walks, frozen when a sheet opens (U-11).
  const [walkIds, setWalkIds] = useState<string[]>([])
  const lastOpened = useRef<string | null>(null)

  // The whole menu, not the first 100: costing is a scan-and-edit job.
  const productsQuery = useQuery({
    queryKey: ['admin-products', 'costing', 'all'],
    queryFn: () => apiClient.getAllProducts(),
  })
  const { data: categories = [] } = useCategories()

  // What checkout would ACTUALLY charge to COGS per product, and why (F-RPT-N25).
  const costingHealthQuery = useQuery({
    queryKey: ['admin-products', 'costing', 'health'],
    queryFn: () => apiClient.getProductCostingHealth().then((res) => res.data ?? []),
  })

  const products = useMemo(() => productsQuery.data ?? [], [productsQuery.data])
  const healthById = useMemo(() => {
    const map = new Map<string, ProductCostingHealth>()
    for (const h of costingHealthQuery.data ?? []) map.set(h.product_id, h)
    return map
  }, [costingHealthQuery.data])

  const statusOf = useCallback((p: Product) => costingStatus(p, healthById.get(p.id)), [healthById])

  const coverage = useMemo(() => {
    const total = products.length
    const costed = products.filter((p) => statusOf(p) !== 'none').length
    const attention = products.filter((p) => statusOf(p) === 'attention').length
    return { total, costed, attention, pct: total === 0 ? 0 : (costed / total) * 100 }
  }, [products, statusOf])

  const overviewQuery = useQuery({
    queryKey: ['reports-overview', 'costing-tab', '30d'],
    queryFn: async () => {
      const today = new Date()
      const start = new Date(today)
      start.setDate(start.getDate() - 29)
      const fmt = (d: Date) => d.toISOString().slice(0, 10)
      return apiClient.getReportsOverview(fmt(start), fmt(today)).then((res) => res.data)
    },
  })
  const salesCoveragePct = overviewQuery.data?.projected_coverage_pct?.current ?? 0
  // F-RPT-N41: when profit is worked out from purchases, "0% of sales came
  // from items that have a cost" reads as a fault. It is not one. Today's
  // basis is the setting in force; the 30-day window covers the days right
  // after a switch, when the window is still mixed.
  const { data: cogsBasis } = useCogsBasis()
  const profitUsesPurchases = cogsBasis?.basis === 'purchases' || overviewQuery.data?.cogs_basis === 'purchases'

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase()
    const rows = products.filter((p) => {
      if (s && !p.name.toLowerCase().includes(s)) return false
      if (categoryFilter !== 'all' && (p.category_id ?? '') !== categoryFilter) return false
      const st = statusOf(p)
      if (statusFilter === 'none' && st !== 'none') return false
      if (statusFilter === 'attention' && st !== 'attention') return false
      if (statusFilter === 'recipe' && st !== 'recipe') return false
      if (statusFilter === 'manual' && st !== 'fixed' && st !== 'percent') return false
      return true
    })
    // Attention first, then uncosted, then the rest — the first row is the one
    // that matters most. Stable within a bucket (menu order).
    const rank: Record<CostingStatus, number> = { attention: 0, none: 1, recipe: 2, fixed: 2, percent: 2 }
    return rows
      .map((p, i) => ({ p, i, r: rank[statusOf(p)] }))
      .sort((a, b) => a.r - b.r || a.i - b.i)
      .map((x) => x.p)
  }, [products, search, categoryFilter, statusFilter, statusOf])

  // Every item that has a recipe, whatever the filter (U-12).
  const copyCandidates: CopyCandidate[] = useMemo(
    () =>
      products
        .filter((p) => (healthById.get(p.id)?.component_count ?? 0) > 0)
        .map((p) => ({ id: p.id, name: p.name, count: healthById.get(p.id)?.component_count ?? 0 }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [products, healthById],
  )

  const selected = itemId ? products.find((p) => p.id === itemId) ?? null : null

  const openItem = useCallback(
    (id: string) => {
      setWalkIds(walkSnapshot(filtered.map((p) => p.id), id))
      onSelectItem(id)
    },
    [filtered, onSelectItem],
  )

  // A deep link opens without a row click: freeze the walk order from the
  // list as it loads, until the owner opens something themselves.
  useEffect(() => {
    if (!itemId) return
    if (!walkIds.includes(itemId) || (walkIds.length <= 1 && filtered.length > 1)) {
      setWalkIds(walkSnapshot(filtered.map((p) => p.id), itemId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, filtered.length])

  // A stale link to an item that no longer exists says so instead of doing nothing (U-20).
  useEffect(() => {
    // Wait for any refetch to finish: a product created a moment ago (U-14)
    // is not in the cached list until the refetch lands.
    if (itemId && productsQuery.isSuccess && !productsQuery.isFetching && !products.some((p) => p.id === itemId)) {
      toastHelpers.error('That item no longer exists', 'It may have been deleted. Showing the full list instead.')
      onSelectItem(null)
    }
  }, [itemId, productsQuery.isSuccess, productsQuery.isFetching, products, onSelectItem])

  // Closing the sheet puts focus back on the row that opened it (U-21).
  useEffect(() => {
    if (itemId) {
      lastOpened.current = itemId
      return
    }
    const last = lastOpened.current
    if (!last) return
    lastOpened.current = null
    requestAnimationFrame(() => {
      document.querySelector<HTMLButtonElement>(`[data-costing-row="${CSS.escape(last)}"]`)?.focus()
    })
  }, [itemId])

  const bulkApply = useCallback(async () => {
    const target = filtered.filter((p) => statusOf(p) === 'none')
    if (target.length === 0) {
      toastHelpers.error('Nothing to apply', 'No filtered items are missing a cost')
      return
    }
    const cost = bulkBasis === 'amount' ? Number(bulkAmount) : null
    const percent = bulkBasis === 'percent' ? Number(bulkPercent) : null
    if (bulkBasis === 'amount' && (Number.isNaN(cost!) || cost! < 0)) {
      toastHelpers.error('Invalid cost', 'Enter a number 0 or greater')
      return
    }
    if (bulkBasis === 'percent' && (Number.isNaN(percent!) || percent! < 0 || percent! > 100)) {
      toastHelpers.error('Invalid percent', 'Enter a number between 0 and 100')
      return
    }
    setBulkBusy(true)
    let ok = 0
    for (const p of target) {
      try {
        await apiClient.updateProduct(p.id, { projected_cost_basis: bulkBasis, projected_cost: cost, projected_cost_percent: percent })
        ok++
      } catch (e) {
        toastHelpers.apiError(`Bulk: ${p.name}`, e)
      }
    }
    setBulkBusy(false)
    qc.invalidateQueries({ queryKey: ['admin-products'] })
    qc.invalidateQueries({ queryKey: ['products'] })
    qc.invalidateQueries({ queryKey: ['reports-overview'] })
    toastHelpers.success('Rough costs applied', `${ok} of ${target.length} updated`)
    setBulkOpen(false)
    setBulkAmount('')
    setBulkPercent('')
  }, [filtered, statusOf, bulkBasis, bulkAmount, bulkPercent, qc])

  return (
    <div className="space-y-6">
      {/* Kitchen flow (Step 4): drinks and other items sold 1 : 1. */}
      <SellAsIsCard />
      {/* Kitchen flow (go-live): the catering menu with its recipes, from a CSV. */}
      <MenuImportCard />
      {/* Coverage */}
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4 sm:p-6">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-600 ring-1 ring-inset ring-amber-500/20 dark:text-amber-400">
              <ChefHat className="h-5 w-5" aria-hidden />
            </span>
            <div className="space-y-1">
              <p className="text-sm font-medium">
                <span className="text-foreground">{coverage.costed}</span> of{' '}
                <span className="text-foreground">{coverage.total}</span> items have a cost
                <span className="text-muted-foreground"> ({coverage.pct.toFixed(0)}%)</span>
                {coverage.attention > 0 && (
                  <>
                    {' '}·{' '}
                    <button type="button" className="text-destructive underline underline-offset-2" onClick={() => setStatusFilter('attention')}>
                      {coverage.attention} need attention
                    </button>
                  </>
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                {profitUsesPurchases ? (
                  ITEM_COSTS_REFERENCE_ONLY
                ) : (
                  <>
                    Last 30 days: <span className="text-foreground">{salesCoveragePct.toFixed(0)}%</span> of sales came from items that have a cost.
                  </>
                )}{' '}
                Click any item to set what it costs you — from its ingredients, a fixed amount, or a percentage.
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)}>
            Rough-in the rest…
          </Button>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          <Input placeholder="Search items…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm flex-1" aria-label="Search items" />
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[200px]" aria-label="Category"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c: Category) => (
                <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <SelectTrigger className="w-[190px]" aria-label="Status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All items</SelectItem>
              <SelectItem value="none">No cost yet</SelectItem>
              <SelectItem value="attention">Needs attention</SelectItem>
              <SelectItem value="recipe">Costed from a recipe</SelectItem>
              <SelectItem value="manual">Costed manually</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* The list */}
      <Card>
        <CardContent className="p-0">
          {productsQuery.isLoading ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading menu…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">No items match the current filters.</div>
          ) : (
            <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Profit</TableHead>
                    <TableHead className="text-right">Margin</TableHead>
                    <TableHead className="w-8"><span className="sr-only">Open</span></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((p) => {
                    const h = healthById.get(p.id)
                    const st = statusOf(p)
                    const reasons = attentionReasons(h)
                    const cost = h?.effective_cost ?? (productHasManualCost(p) ? p.projected_cost! : null)
                    const price = Number(p.price) || 0
                    const profit = cost != null ? price - cost : null
                    const marginPct = cost != null && price > 0 ? ((price - cost) / price) * 100 : null
                    const loss = cost != null && price > 0 && cost > price
                    return (
                      // Mouse users can click anywhere on the row; keyboard and
                      // screen-reader users get a real button in the name cell,
                      // so the row keeps its table semantics (U-21).
                      <TableRow key={p.id} onClick={() => openItem(p.id)} className={cn('cursor-pointer', itemId === p.id && 'bg-muted/60')}>
                        <TableCell className="align-middle">
                          <div className="flex min-w-0 items-center gap-3">
                            {p.image_url ? (
                              <img src={p.image_url} alt="" className="h-9 w-9 shrink-0 rounded-md border bg-muted object-cover" />
                            ) : (
                              <ProductImagePlaceholder className="h-9 w-9 shrink-0" iconClassName="h-4 w-4" />
                            )}
                            <div className="min-w-0">
                              <button
                                type="button"
                                data-costing-row={p.id}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  openItem(p.id)
                                }}
                                className="block max-w-full truncate rounded-sm text-left font-medium hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              >
                                {p.name}
                              </button>
                              {reasons.length > 0 && (
                                <div className="truncate text-xs text-destructive">{reasons.join(' · ')}</div>
                              )}
                              {st === 'recipe' && h && (
                                <div className="text-xs text-muted-foreground">
                                  {h.component_count} ingredient{h.component_count === 1 ? '' : 's'}
                                  {h.components_without_wac > 0 && ' · some costs typed in, not from a receipt'}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{p.category?.name ?? '—'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={cn('font-normal', STATUS_CLASS[st])} title={reasons.join('; ') || undefined}>
                            {COSTING_STATUS_LABELS[st]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{formatMoney(price, currencyCode)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cost != null ? formatMoney(cost, currencyCode) : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className={cn('text-right tabular-nums', loss && 'text-destructive')}>
                          {profit != null ? formatMoney(profit, currencyCode) : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className={cn('text-right tabular-nums', loss && 'text-destructive')}>
                          {marginPct != null ? `${marginPct.toFixed(1)}%` : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-muted-foreground"><ChevronRight className="h-4 w-4" aria-hidden /></TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {selected && (
        <CostingSheet
          product={selected}
          walkIds={walkIds}
          copyCandidates={copyCandidates}
          onNavigate={(id) => onSelectItem(id)}
          onClose={() => onSelectItem(null)}
        />
      )}

      {/* Bulk rough-in — manual modes only; recipes are authored per item. */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rough-in a cost</DialogTitle>
            <DialogDescription className="text-xs">
              Apply one estimate to every item in the current filter that has no cost yet
              ({filtered.filter((p) => statusOf(p) === 'none').length} items). Items that already have a cost or a recipe are left alone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-muted-foreground" aria-hidden />
              <p className="text-xs text-muted-foreground">A quick starting point. Refine the important items with a recipe afterwards.</p>
            </div>
            <div className="inline-flex rounded-md border bg-background p-0.5">
              <button type="button" onClick={() => setBulkBasis('amount')} className={cn('rounded px-3 py-1.5 text-xs font-medium transition-colors', bulkBasis === 'amount' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}>
                Amount ({currencyInputPrefix(currencyCode)})
              </button>
              <button type="button" onClick={() => setBulkBasis('percent')} className={cn('rounded px-3 py-1.5 text-xs font-medium transition-colors', bulkBasis === 'percent' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}>
                % of price
              </button>
            </div>
            {bulkBasis === 'amount' ? (
              <Input type="number" min={0} step={0.01} inputMode="decimal" placeholder="0.00" value={bulkAmount} onChange={(e) => setBulkAmount(e.target.value)} />
            ) : (
              <Input type="number" min={0} max={100} step={0.01} inputMode="decimal" placeholder="35" value={bulkPercent} onChange={(e) => setBulkPercent(e.target.value)} />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkBusy}>Cancel</Button>
            <Button onClick={bulkApply} disabled={bulkBusy}>{bulkBusy ? 'Applying…' : 'Apply to filtered items'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
